import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "../../../utils/toast";
import { API_URL } from "../../../api/api";
import "./letterTemplates.css";

const PLACEHOLDER_FIELDS = [
  { key: "employee_name",         label: "Employee Name",       type: "text" },
  { key: "designation",           label: "Designation",         type: "text" },
  { key: "department",            label: "Department",          type: "text" },
  { key: "start_date",            label: "Start Date",          type: "date" },
  { key: "annual_salary",         label: "Annual Salary (CTC)", type: "text" },
  { key: "monthly_salary",        label: "Monthly Salary",      type: "text" },
  { key: "reporting_manager",     label: "Reporting Manager",   type: "text" },
  { key: "location",              label: "Location",            type: "text" },
  { key: "offer_expiration_date", label: "Offer Expiration Date", type: "date" },
  { key: "effective_date",        label: "Effective Date",      type: "date" },
  { key: "last_working_day",      label: "Last Working Day",    type: "date" },
];

const EditTemplate = () => {
  const { id }   = useParams();
  const navigate = useNavigate();
  const role     = localStorage.getItem("role");
  const userData = JSON.parse(localStorage.getItem("userdata") || "{}");

  const [activeTab,       setActiveTab]       = useState("PREVIEW");
  const [subjectTemplate, setSubjectTemplate] = useState("");
  const [bodyTemplate,    setBodyTemplate]    = useState("");
  const [form,            setForm]            = useState({});
  const [loading,         setLoading]         = useState(true);
  const [saving,          setSaving]          = useState(false);

  /* ── Load template on mount ── */
  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/settings/letter-templates/${id}`);
        setSubjectTemplate(data.subject || "");
        setBodyTemplate(data.body || "");

        const defaults = {};
        PLACEHOLDER_FIELDS.forEach((f) => (defaults[f.key] = ""));

        let preview = {};
        if (data.preview_values) {
          try {
            preview =
              typeof data.preview_values === "string"
                ? JSON.parse(data.preview_values)
                : data.preview_values;
          } catch {}
        }

        setForm({ ...defaults, ...preview });
      } catch (err) {
        console.error("Failed to load template:", err);
        toast.error("Could not load template");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const renderSubject = () => {
    let s = subjectTemplate;
    Object.keys(form).forEach((k) => {
      s = s.replaceAll(`{{${k}}}`, form[k] || "");
    });
    return s;
  };

  const renderBody = () => {
    let b = bodyTemplate;
    Object.keys(form).forEach((k) => {
      b = b.replaceAll(`{{${k}}}`, form[k] || "");
    });
    return b;
  };

  const handleUpdate = async () => {
    if (!subjectTemplate && !bodyTemplate) {
      return toast.error("Both subject and body cannot be empty");
    }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/settings/letter-templates/${id}`, {
        subject    : subjectTemplate,
        body       : bodyTemplate,
        updated_by : userData.id,
      });
      toast.success("Template updated successfully!");
    } catch (err) {
      console.error("Update failed:", err);
      toast.error("Failed to update template");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="lt-loading">
        <Loader2 className="lt-spinner" />
        <span>Loading template…</span>
      </div>
    );
  }

  return (
    <div className="lt-page">
      {/* Header */}
      <div className="lt-page-header">
        <div className="lt-back-header">
          <button
            className="lt-back-btn"
            onClick={() => navigate(`/${role}/settings/letter-templates`)}
          >
            <ArrowLeft size={16} /> Back
          </button>
          <div>
            <h2 className="lt-page-title">Edit Template</h2>
            <p className="lt-page-subtitle">Template ID: {id}</p>
          </div>
        </div>
        <button className="lt-btn-primary" onClick={handleUpdate} disabled={saving}>
          {saving
            ? <><Loader2 size={14} className="lt-spin" /> Updating…</>
            : <><Save size={14} /> Update Template</>}
        </button>
      </div>

      <div className="lt-create-grid">
        {/* ── Left: Preview ── */}
        <div className="lt-create-main">
          <div className="lt-preview-tabs">
            {["PREVIEW", "PDF", "DOCX"].map((tab) => (
              <button
                key={tab}
                className={`lt-preview-tab ${activeTab === tab ? "lt-preview-tab--active" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab} Preview
              </button>
            ))}
          </div>

          <div className="lt-preview-heading">
            <div>
              <h3>Template Editor</h3>
              <p>{new Date().toLocaleString()}</p>
            </div>
          </div>

          <div className="lt-preview-card">
            <div className="lt-subject-preview">
              <span className="lt-subject-label">Subject:</span> {renderSubject()}
            </div>
            <div
              className="lt-body-preview"
              dangerouslySetInnerHTML={{ __html: renderBody().replace(/\n/g, "<br/>") }}
            />
          </div>
        </div>

        {/* ── Right: Controls ── */}
        <div className="lt-create-sidebar">
          {/* Placeholder fill-in */}
          <div className="lt-control-card lt-fields-card">
            <div className="lt-control-label">Placeholder Values (Preview Only)</div>
            {PLACEHOLDER_FIELDS.map((field) => (
              <div key={field.key} className="lt-field">
                <label className="lt-label">{field.label}</label>
                <input
                  className="lt-input"
                  type={field.type}
                  name={field.key}
                  value={form[field.key] || ""}
                  onChange={handleChange}
                />
              </div>
            ))}
          </div>

          {/* Subject template */}
          <div className="lt-control-card">
            <div className="lt-control-label">Subject Template</div>
            <textarea
              className="lt-textarea lt-textarea--sm"
              rows={2}
              value={subjectTemplate}
              onChange={(e) => setSubjectTemplate(e.target.value)}
            />
          </div>

          {/* Body template */}
          <div className="lt-control-card">
            <div className="lt-control-label">Body Template (HTML / Plain Text)</div>
            <textarea
              className="lt-textarea"
              rows={12}
              value={bodyTemplate}
              onChange={(e) => setBodyTemplate(e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditTemplate;
